// Ported from ops-portal/src/app/pages/manage-businesses/confirm-block-modal/
// Collects an optional description, then calls freezeUnfreeze and returns the result.
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { M3ApiResponse } from '@/types/api';
import type { BusinessEntityDetail, BusinessEntityMember, BusinessEntityOwner } from '@/types/business-entity';
import { useFreezeUnfreezeMutation } from './api';

export interface ConfirmBlockModalProps {
  be: Pick<BusinessEntityDetail, 'businessEntityId' | 'companyName'>;
  /** Team member (isBe=false) or primary owner (isBe=true). */
  member: Pick<BusinessEntityMember, 'userId'> | Pick<BusinessEntityOwner, 'userId'> | null | undefined;
  /** true = freeze/block, false = restore. */
  block: boolean;
  /** true = action targets the BE itself; false = targets an individual member. */
  isBe: boolean;
  onClose: (result: M3ApiResponse | null) => void;
}

export function ConfirmBlockModal({ be, member, block, isBe, onClose }: ConfirmBlockModalProps) {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const mutation = useFreezeUnfreezeMutation();

  const titleKey = isBe
    ? block
      ? 'pages.single_business.description_modal.block_be.title'
      : 'pages.single_business.description_modal.restore_be.title'
    : block
      ? 'pages.single_business.description_modal.block_user.title'
      : 'pages.single_business.description_modal.restore_user.title';

  const subtitleKey = isBe
    ? block
      ? 'pages.single_business.description_modal.block_be.subtitle'
      : 'pages.single_business.description_modal.restore_be.subtitle'
    : block
      ? 'pages.single_business.description_modal.block_user.subtitle'
      : 'pages.single_business.description_modal.restore_user.subtitle';

  const handleContinue = async () => {
    const result = await mutation.mutateAsync({
      businessEntityId: be.businessEntityId,
      userId: member?.userId ?? '',
      freeze: block,
      description,
    });
    onClose(result);
  };

  return (
    <Dialog open onClose={() => onClose(null)} disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle>
        {t(titleKey, { defaultValue: block ? 'Block' : 'Restore' })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t(subtitleKey, { defaultValue: '' })}
        </Typography>
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('pages.single_business.description_modal.placeholder', {
            defaultValue: 'Add a description…',
          })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>
          {t('global.buttons.cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          variant="contained"
          color={block ? 'error' : 'success'}
          disabled={description.length < 1 || mutation.isPending}
          onClick={() => { void handleContinue(); }}
        >
          {t('global.buttons.continue', { defaultValue: 'Continue' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
